/**
 * Ask Bernoulli bridge for the Safe Haven Scorecard (same protocol as alloc-tool's bridge).
 *
 * askbernoulli.ai (parent window) ⇄ this app (iframe):
 *  - ASK_BERNOULLI_EMBED / READY / REQUEST_SNAPSHOT → we answer TOOL_SNAPSHOT { allocConfig: scorecardConfig }
 *  - CHAT_TO_TOOL { code: [{ 'data-testid', value, message }], messageId, mode } → Play runs the steps,
 *    reports TOOL_TO_CHAT code_progress (one per step) and code_complete, then sends a fresh snapshot.
 *
 * The snapshot is { fromPage, fromPageConfig, fromAppPayload, fromUiState } — crewAI reads it as `scorecardConfig`
 * (ai_chatbot puts it under that request key; the payload also says `allocConfig` because the chat's iframe pane
 * reads `payload.allocConfig || payload`).
 */
import { applyDomAutomation, collectDomDataTestIds } from '@unianlx-support/ua-ui-lib';

import { store } from '@/store';
import { FROM_PAGE } from '@/constants/app';
import { isEmbeddedApp } from '@/utils/embedMode';
import { testIdBelongsToFromPage } from '@/utils/scorecardKbPageMatch';

const BRIDGE_SOURCE = 'ask-bernoulli';
const TYPES = {
  READY: 'ASK_BERNOULLI_READY',
  CHAT_TO_TOOL: 'ASK_BERNOULLI_CHAT_TO_TOOL',
  TOOL_TO_CHAT: 'ASK_BERNOULLI_TOOL_TO_CHAT',
  REQUEST_SNAPSHOT: 'ASK_BERNOULLI_REQUEST_SNAPSHOT',
  TOOL_SNAPSHOT: 'ASK_BERNOULLI_TOOL_SNAPSHOT',
  ACK: 'ASK_BERNOULLI_ACK',
  // the host's Asset Manager saved or deleted a user asset: refetch the user-asset catalog, no reload needed
  ASSETS_CHANGED: 'ASK_BERNOULLI_ASSETS_CHANGED',
};
const EVENTS = {
  CODE_PROGRESS: 'code_progress',
  CODE_COMPLETE: 'code_complete',
};

// Dev-only controls (the JS / Python parity toolbar) are never part of the snapshot or of Play.
const HIDDEN_TESTID_PREFIXES = ['main-parity-'];
// Settings kept in Redux `portfolio` that describe the screen (both tabs share one slice).
const UI_KEYS = [
  'mode', 'selectedType', 'fromYear', 'toYear', 'regimeCount', 'regimeSelectors', 'firstAsset', 'firstAssetName',
  // Single Asset tab
  'secondAsset', 'secondAssetName', 'alloc', 'lock', 'insuranceSliderValue', 'percentileDropdown',
  // Different Assets tab
  'assets', 'activeAsset', 'secondAssets', 'secondAssetNames', 'allocByAsset', 'insuranceSliderValues', 'allocLock',
  'pin', 'percentile1Dropdown', 'percentile2Dropdown',
];
const ASSET_SELECTS = {
  base: ['main-returns-first-asset-select'],
  other: ['main-second-asset-select', 'main-multi-asset-1-second-asset-select'],
};
const optionCache = { base: null, other: null };

function isTrustedHostOrigin(origin) {
  try {
    const { hostname, protocol } = new URL(origin);
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
    if (protocol !== 'https:') return false;
    return hostname === 'askbernoulli.ai' || hostname.endsWith('.askbernoulli.ai');
  } catch {
    return false;
  }
}

function parseData(data) {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data && typeof data === 'object' ? data : null;
}

function isBridgeMessage(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.source && data.source !== BRIDGE_SOURCE) return false;
  return Object.values(TYPES).includes(data.type);
}

function payloadOf(data) {
  if (data.payload && typeof data.payload === 'object') return data.payload;
  const rest = { ...data };
  delete rest.source;
  delete rest.type;
  delete rest.id;
  return rest;
}

function isHiddenTestId(testId) {
  return HIDDEN_TESTID_PREFIXES.some((prefix) => String(testId || '').startsWith(prefix));
}

/** The options of the first asset dropdown found on screen ([{ value, text, group }]); last seen when hidden. */
function readSelectOptions(kind) {
  if (typeof document === 'undefined') return optionCache[kind];
  for (const testId of ASSET_SELECTS[kind]) {
    const select = document.querySelector(`[data-testid="${testId}"]`);
    if (!select || !select.options) continue;
    const options = Array.from(select.options).map((option) => ({
      value: option.value,
      text: option.text,
      group: option.parentElement && option.parentElement.tagName === 'OPTGROUP' ? option.parentElement.label : '',
    }));
    if (options.length) {
      optionCache[kind] = options;
      return options;
    }
  }
  return optionCache[kind];
}

/** What the engine used on screen (the allocation the mix figures are computed at, per mode / column). */
function appliedAllocations(portfolio) {
  const response = portfolio?.response;
  if (!response || typeof response !== 'object') return null;
  const mode = portfolio.selectedType || 'normal';
  if (response.columns && typeof response.columns === 'object') {
    const out = {};
    Object.entries(response.columns).forEach(([id, column]) => {
      const mix = column?.modes?.[mode]?.mix;
      if (mix && mix.alloc != null) out[id] = Number(mix.alloc);
    });
    return Object.keys(out).length ? out : null;
  }
  const mix = response.modes?.[mode]?.mix;
  return mix && mix.alloc != null ? { single: Number(mix.alloc) } : null;
}

export function collectScorecardConfig() {
  const state = store.getState();
  const portfolio = state.portfolio || {};
  const pageConfig = collectDomDataTestIds(FROM_PAGE.MAIN, undefined, (testId, fromPage) =>
    !isHiddenTestId(testId) && testIdBelongsToFromPage(testId, fromPage)
  );
  const ui = {};
  UI_KEYS.forEach((key) => {
    if (portfolio[key] !== undefined) ui[key] = portfolio[key];
  });
  ui.loading = Boolean(portfolio.loading);
  ui.error = portfolio.error || null;
  ui.lastUpdatedAt = portfolio.lastUpdatedAt || null;
  ui.appliedAllocations = appliedAllocations(portfolio);
  ui.regimeBins = portfolio.response?.regime?.bins || null;
  ui.regimeYearCounts = (portfolio.response?.regime?.regimeBar?.datasets?.[0]?.data || []).map((d) => (d && typeof d === 'object' ? d.y : d));
  ui.baseOptions = readSelectOptions('base');
  ui.otherOptions = readSelectOptions('other');
  return {
    fromPage: FROM_PAGE.MAIN,
    fromPageConfig: pageConfig,
    fromAppPayload: portfolio.requestPayload || null,
    fromUiState: ui,
  };
}

function postToParent(type, payload, targetOrigin) {
  if (!window.parent || window.parent === window) return;
  window.parent.postMessage(
    {
      source: BRIDGE_SOURCE,
      type,
      id: `bridge_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      payload,
    },
    targetOrigin || '*',
  );
}

function sendSnapshot(targetOrigin) {
  const scorecardConfig = collectScorecardConfig();
  postToParent(TYPES.TOOL_SNAPSHOT, { allocConfig: scorecardConfig, scorecardConfig }, targetOrigin);
}

/**
 * The scorecard only recalculates when a slider is released (onMouseUp / onTouchEnd). Play sets a range input with
 * input + change events, so while Play runs every change on a plain range input is followed by a mouseup on that
 * input (MUI's regime slider commits through its own hidden input and is left alone).
 */
function installSliderCommit() {
  if (typeof document === 'undefined') return () => {};
  const onChange = (event) => {
    const el = event.target;
    if (!el || el.tagName !== 'INPUT' || (el.getAttribute('type') || '').toLowerCase() !== 'range') return;
    if (el.hasAttribute('data-index')) return; // MUI slider thumb input: commits itself
    window.setTimeout(() => {
      el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }));
    }, 0);
  };
  document.addEventListener('change', onChange, true);
  return () => document.removeEventListener('change', onChange, true);
}

function waitForIdle(timeoutMs = 30000) {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const loading = Boolean(store.getState()?.portfolio?.loading);
      if (!loading || Date.now() - started > timeoutMs) {
        resolve();
        return;
      }
      window.setTimeout(tick, 150);
    };
    window.setTimeout(tick, 350); // the screen waits 250 ms before it calls the engine
  });
}

function playableSteps(code) {
  return (Array.isArray(code) ? code : []).filter(
    (step) => step && step['data-testid'] && !isHiddenTestId(step['data-testid'])
  );
}

async function runCode(code, options = {}) {
  const steps = playableSteps(code);
  if (!steps.length) return [];
  const removeCommit = installSliderCommit();
  const reported = new Set();
  try {
    await applyDomAutomation(steps, {
      mode: options.mode === 'direct' ? 'direct' : 'guided',
      store,
      onStepProgress: (index) => {
        // index = the step about to run; every earlier step is done
        for (let done = 0; done < Math.min(index, steps.length); done += 1) {
          if (reported.has(done)) continue;
          reported.add(done);
          options.onStep?.(steps[done], done, steps.length, 'applied');
        }
      },
    });
    for (let done = 0; done < steps.length; done += 1) {
      if (!reported.has(done)) options.onStep?.(steps[done], done, steps.length, 'applied');
    }
    await waitForIdle();
  } finally {
    removeCommit();
  }
  return steps.map((step) => ({ ...step, outcome: 'applied' }));
}

export function startAskBernoulliBridge() {
  if (typeof window === 'undefined') return () => {};
  if (!isEmbeddedApp()) return () => {};

  let pendingJob = null;
  let running = false;

  const drainCode = async (targetOrigin) => {
    if (running) return;
    const job = pendingJob;
    pendingJob = null;
    if (!job?.code?.length) return;
    running = true;
    let report = [];
    try {
      report = await runCode(job.code, {
        mode: job.mode,
        onStep: (step, index, total, outcome) =>
          postToParent(
            TYPES.TOOL_TO_CHAT,
            { event: EVENTS.CODE_PROGRESS, messageId: job.messageId || '', index, total, outcome, label: step.message || '' },
            targetOrigin,
          ),
      });
    } catch (error) {
      console.error('[AskBernoulli bridge] Play failed', error);
      report = playableSteps(job.code).map((step) => ({ ...step, outcome: 'failed' }));
    } finally {
      sendSnapshot(targetOrigin);
      postToParent(
        TYPES.TOOL_TO_CHAT,
        { event: EVENTS.CODE_COMPLETE, messageId: job.messageId || '', steps: report },
        targetOrigin,
      );
      running = false;
      if (pendingJob?.code?.length) void drainCode(targetOrigin);
    }
  };

  const onMessage = (event) => {
    if (!isTrustedHostOrigin(event.origin)) return;
    const data = parseData(event.data);
    if (!data) return;

    if (data.type === 'ASK_BERNOULLI_EMBED') {
      sendSnapshot(event.origin);
      return;
    }
    if (!isBridgeMessage(data)) return;
    const payload = payloadOf(data);

    if (data.type === TYPES.READY || data.type === TYPES.REQUEST_SNAPSHOT) {
      sendSnapshot(event.origin);
      if (pendingJob?.code?.length) void drainCode(event.origin);
      return;
    }

    if (data.type === TYPES.CHAT_TO_TOOL && payload.code?.length) {
      pendingJob = {
        code: payload.code,
        messageId: payload.messageId || '',
        mode: payload.mode === 'direct' ? 'direct' : 'guided',
      };
      void drainCode(event.origin);
      return;
    }

    if (data.type === TYPES.ASSETS_CHANGED) {
      // the app's asset loaders listen for this and take the latest list from the API
      window.dispatchEvent(new CustomEvent('ab:userAssetsChanged'));
    }
  };

  window.addEventListener('message', onMessage);
  sendSnapshot();
  return () => window.removeEventListener('message', onMessage);
}
